import {PreventativeAutocompleteType} from "../../../../views/panel/operator/settings/import/engine/preventative-interactions/shared";
import {
    joinDelimitedList,
    parseDelimitedList,
} from "../../../../views/panel/operator/settings/import/engine/transformers/shared/inventory-availability-validation";
import {AdjustmentPreventionInvalidityCode} from "./invalidity-codes";

/**
 * Prefills the preventative text input from a formatted serial list or delimited string.
 * @param {*} val
 * @returns {string}
 */
const extractSerialNumbersValue = (val) => {
    if (Array.isArray(val)) {
        return joinDelimitedList(val, ', ');
    }
    if (val == null) {
        return '';
    }
    return String(val);
};

/**
 * @param {*} val
 * @returns {string[]}
 */
const formatSerialsForRow = (val) => parseDelimitedList(val);

/**
 * @param {string} key
 * @returns {Object}
 */
const createPartField = (key = 'partNo') => ({
    key,
    label: 'Part Number',
    type: PreventativeAutocompleteType.PART,
    extractValue: (o) => {
        if (typeof o === 'string') {
            return o;
        }
        return o?.partNo ?? o?.partNumber ?? null;
    },
    modifyRow: (row, val) => {
        const next = typeof val === 'string' ? val.trim() : val;
        if (next == null || next === '') {
            return row;
        }
        return {...row, [key]: String(next).toUpperCase()};
    },
    autocompleteProps: {
        placeholder: 'Search for part...',
        getOptionLabel: (o) => (typeof o === 'string' ? o : o?.partNo ?? o?.partNumber ?? ''),
        isOptionEqualToValue: (o, v) => {
            const optionPart = typeof o === 'string' ? o : o?.partNo ?? o?.partNumber;
            const valuePart = typeof v === 'string' ? v : v?.partNo ?? v?.partNumber;
            return optionPart != null && valuePart != null && optionPart === valuePart;
        },
    },
});

/**
 * @param {string} key
 * @returns {Object}
 */
const createWarehouseField = (key = 'warehouseCode') => ({
    key,
    label: 'Warehouse',
    type: PreventativeAutocompleteType.WAREHOUSE,
    extractValue: (o) => {
        if (typeof o === 'string') {
            return o;
        }
        return o?.code ?? o?.warehouseCode ?? null;
    },
    modifyRow: (row, val) => {
        const next = typeof val === 'string' ? val.trim() : val;
        if (next == null || next === '') {
            return row;
        }
        return {...row, [key]: next};
    },
    autocompleteProps: {
        placeholder: 'Search for warehouse...',
        getOptionLabel: (o) => {
            if (typeof o === 'string') {
                return o;
            }
            return o?.name ? `${o.name} (${o.code})` : o?.code ?? o?.warehouseCode ?? '';
        },
        isOptionEqualToValue: (o, v) => {
            const optionCode = typeof o === 'string' ? o : o?.code ?? o?.warehouseCode;
            const valueCode = typeof v === 'string' ? v : v?.code ?? v?.warehouseCode;
            return optionCode != null && valueCode != null
                && String(optionCode).toUpperCase() === String(valueCode).toUpperCase();
        },
    },
});

/**
 * Preventative correction sections for inventory adjustment CSV import.
 * Passed to the adjustment-colocated PreventativeCorrectionDialog via `sectionConfigs`.
 */
export const adjustmentImportPreventativeSections = [
    {
        sectionKey: 'invalidPart',
        sectionTitle: 'Invalid Part Numbers',
        invalidityCode: AdjustmentPreventionInvalidityCode.INVALID_PART,
        fields: [createPartField('partNo')],
    },
    {
        sectionKey: 'invalidWarehouse',
        sectionTitle: 'Invalid Warehouse Codes',
        invalidityCode: AdjustmentPreventionInvalidityCode.INVALID_WAREHOUSE,
        fields: [createWarehouseField('warehouseCode')],
    },
    {
        sectionKey: 'serialCountMismatch',
        sectionTitle: 'Serial Count Does Not Match Quantity',
        invalidityCode: AdjustmentPreventionInvalidityCode.SERIAL_COUNT_MISMATCH,
        fields: [
            {
                key: 'quantity',
                label: 'Quantity (signed: + add / − remove)',
                type: 'input',
                textFieldProps: {inputProps: {type: 'number'}},
                modifyRow: (row, val) => {
                    const parsed = Number(val);
                    return {
                        ...row,
                        quantity: Number.isNaN(parsed) ? row.quantity : parsed,
                    };
                },
            },
            {
                key: 'serialNos',
                label: 'Serial Numbers (comma-separated, distinct)',
                type: 'input',
                extractValue: extractSerialNumbersValue,
                modifyRow: (row, val) => ({
                    ...row,
                    serialNos: formatSerialsForRow(val),
                }),
            },
        ],
    },
    {
        sectionKey: 'duplicatePartRow',
        sectionTitle: 'Duplicate Part + Warehouse Rows',
        invalidityCode: AdjustmentPreventionInvalidityCode.DUPLICATE_PART_ROW,
        fields: [
            {
                ...createPartField('partNo'),
                label: 'Part No',
            },
            createWarehouseField('warehouseCode'),
            {
                key: 'quantity',
                label: 'Quantity (signed)',
                type: 'input',
                textFieldProps: {inputProps: {type: 'number'}},
                modifyRow: (row, val) => {
                    const parsed = Number(val);
                    return {
                        ...row,
                        quantity: Number.isNaN(parsed) ? row.quantity : parsed,
                    };
                },
            },
            {
                key: 'serialNos',
                label: 'Serial Numbers',
                type: 'input',
                extractValue: extractSerialNumbersValue,
                modifyRow: (row, val) => ({
                    ...row,
                    serialNos: formatSerialsForRow(val),
                }),
            },
        ],
    },
];
